import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import Awesomplete from "awesomplete";

/**
 * Initializes WPD dataSeriesManagement object.
 */
function init(wpd, wpdDocument, dataEntryVM) {
	const datasetAutocompletes = [];
	const tagGroupAutocompletes = [];
	const allTagGroups = dataEntryVM.getTagGroups();
	const tagGroups = [
		_.find(allTagGroups, { name: "Outcomes" }),
		_.find(allTagGroups, { name: "Baseline/Demographics" }),
		_.find(allTagGroups, { name: "Adverse events" }),
	];

	let container = null;
	let saveButton = null;
	let invalidCount = 0;
	let defaultTagGroup = null;

	// dataset input select handler, triggers validation
	const datasetSelectHandler = ({ originalEvent }) => {
		const input = originalEvent.target
			.closest(".add-dataset-row-actions")
			.querySelector(".add-dataset-study-arm-input");

		wpd.dataSeriesManagement.validateRow(input, false);
	};

	const editDatasetSelectHandler = ({ originalEvent }) => {
		const input = originalEvent.target
			.closest(".edit-dataset-row-actions")
			.querySelector(".edit-dataset-study-arm-input");

		wpd.dataSeriesManagement.validateRow(input, true);
	};

	// tag group input select handler, triggers validation
	const tagGroupSelectHandler = ({ originalEvent }) => {
		const input = originalEvent.target
			.closest(".add-dataset-row-actions")
			.querySelector(".add-dataset-tag-group-input");

		wpd.dataSeriesManagement.validateRow(input, false);
	};

	const editTagGroupSelectHandler = ({ originalEvent }) => {
		const input = originalEvent.target
			.closest(".edit-dataset-row-actions")
			.querySelector(".edit-dataset-tag-group-input");

		wpd.dataSeriesManagement.validateRow(input, true);
	};

	// row inputs blur handler
	const rowInputBlur = function ({ target }) {
		wpd.dataSeriesManagement.validateRow(target, false);
	};

	const editRowInputBlur = function ({ target }) {
		wpd.dataSeriesManagement.validateRow(target, true);
	};

	// row inputs keyup handler
	const rowInputKeyUp = _.debounce(
		function (event) {
			// check for shift+enter key stroke
			if (event.shiftKey && event.key === "Enter") {
				wpd.dataSeriesManagement.addRow(true, false);
			}
		},
		200,
		{
			leading: true,
		},
	);

	// click handler for autocompletes to allow user to see the options
	const clickHandler = (event) => {
		const index = event.target.closest(".add-dataset-row").dataset.index;

		let awesomplete;
		if (event.target.classList.contains("add-dataset-study-arm-input")) {
			awesomplete = datasetAutocompletes[index];
		} else {
			awesomplete = tagGroupAutocompletes[index];
		}

		if (awesomplete.ul.childNodes.length === 0) {
			awesomplete.minChars = 0;
			awesomplete.evaluate();
		} else if (awesomplete.ul.hasAttribute("hidden")) {
			awesomplete.open();
		} else {
			awesomplete.close();
		}
	};

	const editClickHandler = (event) => {
		const index = event.target.closest(".edit-dataset-row").dataset.index;

		let awesomplete;
		if (event.target.classList.contains("edit-dataset-study-arm-input")) {
			awesomplete = datasetAutocompletes[index];
		} else {
			awesomplete = tagGroupAutocompletes[index];
		}

		if (awesomplete.ul.childNodes.length === 0) {
			awesomplete.minChars = 0;
			awesomplete.evaluate();
		} else if (awesomplete.ul.hasAttribute("hidden")) {
			awesomplete.open();
		} else {
			awesomplete.close();
		}
	};

	const checkboxHandler = (event) => {
		if (event.target.checked) {
			setAllTagGroupInputs();
		} else {
			// clear default value for new tag group inputs
			defaultTagGroup = null;
		}
	};

	const setAllTagGroupInputs = () => {
		const tagGroupCheckbox = container.children[0].querySelector(
			".add-dataset-same-tag-groups-checkbox",
		);

		if (tagGroupCheckbox.checked) {
			const tagGroupInputs = container.getElementsByClassName(
				"add-dataset-tag-group-input",
			);

			// set default value for new tag group inputs
			defaultTagGroup = tagGroupInputs[0].value.trim();

			// set any existing tag group inputs to target value
			Array.prototype.forEach.call(tagGroupInputs, (tagGroupInput, index) => {
				if (index > 0) {
					tagGroupInput.value = defaultTagGroup;
					wpd.dataSeriesManagement.validateRow(tagGroupInput);
				}
			});
		}
	};

	// remove all listeners for a pair of dataset and tag group inputs
	const removeListeners = (
		datasetInput,
		tagGroupInput,
		tagGroupCheckbox,
		isEdit,
	) => {
		datasetInput.removeEventListener(
			"awesomplete-selectcomplete",
			isEdit ? editDatasetSelectHandler : datasetSelectHandler,
		);
		datasetInput.removeEventListener(
			"click",
			isEdit ? editClickHandler : clickHandler,
		);
		datasetInput.removeEventListener(
			"blur",
			isEdit ? editRowInputBlur : rowInputBlur,
		);

		if (!isEdit) {
			datasetInput.removeEventListener("keyup", rowInputKeyUp);
		}

		tagGroupInput.removeEventListener(
			"awesomplete-selectcomplete",
			isEdit ? editTagGroupSelectHandler : tagGroupSelectHandler,
		);
		tagGroupInput.removeEventListener(
			"click",
			isEdit ? editClickHandler : clickHandler,
		);
		tagGroupInput.removeEventListener(
			"blur",
			isEdit ? editRowInputBlur : rowInputBlur,
		);

		if (!isEdit) {
			tagGroupInput.removeEventListener("keyup", rowInputKeyUp);
		}

		tagGroupCheckbox?.removeEventListener("click", checkboxHandler);
	};

	// attach all listeners for a pair of dataset and tag group inputs
	const attachListeners = (
		datasetInput,
		tagGroupInput,
		tagGroupCheckbox,
		isEdit,
	) => {
		removeListeners(datasetInput, tagGroupInput, tagGroupCheckbox, isEdit);

		datasetInput.addEventListener(
			"awesomplete-selectcomplete",
			isEdit ? editDatasetSelectHandler : datasetSelectHandler,
		);
		datasetInput.addEventListener(
			"click",
			isEdit ? editClickHandler : clickHandler,
		);
		datasetInput.addEventListener(
			"blur",
			isEdit ? editRowInputBlur : rowInputBlur,
		);

		if (!isEdit) {
			datasetInput.addEventListener("keyup", rowInputKeyUp);
		}

		tagGroupInput.addEventListener(
			"awesomplete-selectcomplete",
			isEdit ? editTagGroupSelectHandler : tagGroupSelectHandler,
		);
		tagGroupInput.addEventListener(
			"click",
			isEdit ? editClickHandler : clickHandler,
		);
		tagGroupInput.addEventListener(
			"blur",
			isEdit ? editRowInputBlur : rowInputBlur,
		);

		if (!isEdit) {
			tagGroupInput.addEventListener("keyup", rowInputKeyUp);
		}

		tagGroupCheckbox?.addEventListener("click", checkboxHandler);
	};

	// updated element reference and dataset name generation function call
	wpd.dataSeriesManagement.showAddDataset = function (isEdit) {
		wpd.popup.show("add-dataset-popup");

		container = wpdDocument.getElementById("axis-study-branch-inputs");

		const editButtons = wpdDocument.getElementById(
			"graph-edit-study-branch-buttons",
		);
		const addButtons = wpdDocument.getElementById(
			"graph-add-study-branch-buttons",
		);

		if (isEdit) {
			editButtons.removeAttribute("hidden");
			addButtons.setAttribute("hidden", "true");
			saveButton = wpdDocument.getElementById("edit-dataset-save-button");
		} else {
			editButtons.setAttribute("hidden", "true");
			addButtons.removeAttribute("hidden");
			saveButton = wpdDocument.getElementById("add-dataset-save-button");
		}

		const sameTagGroupContainer = container.children[0].querySelector(
			".add-dataset-same-tag-groups-container",
		);
		if (isEdit) {
			sameTagGroupContainer.setAttribute("hidden", true);
		} else {
			sameTagGroupContainer.removeAttribute("hidden");
		}

		wpd.dataSeriesManagement.initializeAutocompletes(false);

		// stamp dataset uuid on the static first row so per-branch broadcast
		// scope references can use it as soon as the curator types a branch
		container.children[0].dataset.datasetUuid = uuidv4();

		// reset per-branch broadcast tag section; setStudyBranchFieldValues
		// (edit mode) populates afterward, then we sync + load existing entries
		wpdDocument
			.getElementById("branch-broadcast-tags-groups")
			?.replaceChildren();
		wpdDocument
			.getElementById("branch-broadcast-tags-section")
			?.setAttribute("hidden", "true");

		// disable save button
		saveButton.disabled = true;
	};

	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	wpd.dataSeriesManagement.initializeAutocompletes = function (isSidebar) {
		const compositeKeys = wpd.tagGroups.compositeKeys;
		const prefix = isSidebar ? "add-" : "";
		container = wpdDocument.getElementById(prefix + "axis-study-branch-inputs");
		const datasetNames = compositeKeys.map((key) => key.key);

		const datasetInput = container.children[0].querySelector(
			".add-dataset-study-arm-input",
		);
		const tagGroupInput = container.children[0].querySelector(
			".add-dataset-tag-group-input",
		);
		const tagGroupCheckbox = container.children[0].querySelector(
			".add-dataset-same-tag-groups-checkbox",
		);

		// initialize autocompletes
		datasetAutocompletes[0] = this._compositeKeyInput(
			datasetInput,
			datasetNames,
		);

		tagGroupAutocompletes[0] = this._tagGroupInput(tagGroupInput, tagGroups);

		// add event listeners
		attachListeners(datasetInput, tagGroupInput, tagGroupCheckbox, false);

		// add on blur listener for first tag group input only
		tagGroupInput.removeEventListener("blur", setAllTagGroupInputs);
		tagGroupInput.addEventListener("blur", setAllTagGroupInputs);
	};

	wpd.dataSeriesManagement._compositeKeyInput = function (input, datasetNames) {
		const awesomplete = new Awesomplete(input, {
			list: datasetNames,
			autoFirst: false,
			maxItems: 20,
			minChars: 0,
			item: function (text, input) {
				const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(text);
				const itemName =
					parsedKey.subgroup ||
					parsedKey.population ||
					parsedKey.studyArm ||
					parsedKey.studyPart ||
					parsedKey.study;

				const population = parsedKey.subgroup ? parsedKey.population : "";
				const arm =
					parsedKey.subgroup || parsedKey.population ? parsedKey.studyArm : "";
				const part = parsedKey.studyArm ? parsedKey.studyPart : "";
				const study =
					parsedKey.studyArm || parsedKey.studyPart ? parsedKey.study : "";
				let htmlElement = document.createElement("li");
				htmlElement.innerHTML = `
		<div class='composite-key-selector-container'>
		<span class='composite-key-selector-key'>${itemName}</span>
		<div class='composite-key-selector-path-container'>${
			population
				? `<span class='composite-key-selector-path'>${population}</span>`
				: ""
		}${arm ? `<span class='composite-key-selector-path'>${arm}</span>` : ""}${
					part ? `<span class='composite-key-selector-path'>${part}</span>` : ""
				}${
					study
						? `<span class='composite-key-selector-path'>${study}</span>`
						: ""
				}</div></div>`;
				return htmlElement;
			},
			replace: function (suggestion) {
				const key = suggestion.label;
				const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(key);
				this.input.value =
					parsedKey.subgroup ||
					parsedKey.population ||
					parsedKey.studyArm ||
					parsedKey.studyPart ||
					parsedKey.study;

				this.input.setAttribute("data-composite-key", key);
			},
			container: function (input) {
				var container = Awesomplete.$.create("div", {
					className: "awesomplete composite-key-awesomplete",
				});
				input.parentNode.insertBefore(container, input);
				return container;
			},
		});

		return awesomplete;
	};

	wpd.dataSeriesManagement._tagGroupInput = function (input, tagGroups) {
		return new Awesomplete(input, {
			list: tagGroups,
			maxItems: 20,
			minChars: 0,
			data: function (item) {
				return {
					label: item.name,
					value: item.uuid,
				};
			},
			// insert label instead of value into the input
			replace: function (suggestion) {
				this.input.value = suggestion.label;
			},
		});
	};

	// function to generate dataset name input given an index
	wpd.dataSeriesManagement._generateDatasetNameInput = function (index) {
		return `
			<div class="add-dataset-row" data-index="${index}">
				<div class="add-dataset-row-label">
					Study Branch <span class="add-dataset-number">${index + 1}</span>:
				</div>

				<div class="add-dataset-row-actions">
					<input
						type="text"
						class="add-dataset-study-arm-input"
						data-type="composite-key"
					/>

					<input
						type="text"
						class="add-dataset-tag-group-input"
						data-type="tag-group"
					/>

					<input
						type="button"
						value="⌫"
						class="dataset-remove-button"
						onclick="wpd.dataSeriesManagement.removeRow(event);"
					/>

					<br />

					<span class="uneditable-message"></span>
					<span class="error-message"></span>
				</div>
			</div>
		`;
	};

	wpd.dataSeriesManagement._generateSidebarDatasetNameInput = function (index) {
		return `
			<div class="add-dataset-row" data-index="${index}" style="display: block; border-top: 1px solid #ddd; padding-top: 0px;">
				<div style="font-size: 0.9em; font-weight: bold; display: flex; justify-content: space-between; align-items: center;">
					<span>Study Branch <span class="add-dataset-number">${index + 1}</span></span>
					<button
						class="dataset-remove-button"
						onclick="wpd.dataSeriesManagement.removeRow(event);"
						style="border: none; background: none; cursor: pointer; font-size: 1.5em; padding: 0 4px; color: #666; line-height: 1;"
						onmouseover="this.style.color='#c00';"
						onmouseout="this.style.color='#666';"
						title="Remove study branch"
					>x</button>
				</div>

				<div class="add-dataset-row-actions" style="display: block;">
					<label style="font-size: 0.9em; display: block;">Study branch</label>
					<input
						type="text"
						class="add-dataset-study-arm-input"
						data-type="composite-key"
						placeholder="Study branch"
						style="width: 240px;"
					/>

					<div style="display: flex; align-items: flex-end; gap: 6px;">
						<div style="flex: 1;">
							<label style="font-size: 0.9em; display: block;">Tag group</label>
							<input
								type="text"
								class="add-dataset-tag-group-input"
								data-type="tag-group"
								placeholder="Tag group"
								style="width: 240px;"
							/>
						</div>
					</div>
					<span class="uneditable-message" style="display: block; font-size: 0.85em; color: #666;"></span>
					<span class="error-message" style="display: block; font-size: 0.85em; color: red;"></span>
				</div>
			</div>
		`;
	};

	// function to reset add dataset dialog
	wpd.dataSeriesManagement.resetDialog = function (isEdit) {
		const inputs = container.children;

		// destroy all instances of awesomplete
		for (let i = 0; i < datasetAutocompletes.length; i++) {
			datasetAutocompletes[i].destroy();
			tagGroupAutocompletes[i].destroy();
		}
		datasetAutocompletes.length = 0;
		tagGroupAutocompletes.length = 0;

		const classPrefix = isEdit ? "edit" : "add";

		// remove the rest and set the value of the first name input to ""
		while (inputs.length > 1) {
			// remove select event handler
			const datasetInput = inputs[inputs.length - 1].querySelector(
				`.${classPrefix}-dataset-study-arm-input`,
			);
			const tagGroupInput = inputs[inputs.length - 1].querySelector(
				`.${classPrefix}-dataset-tag-group-input`,
			);

			removeListeners(datasetInput, tagGroupInput, null, isEdit);

			inputs[inputs.length - 1].remove();
		}

		const datasetInput = inputs[0].querySelector(
			`.${classPrefix}-dataset-study-arm-input`,
		);

		datasetInput.removeAttribute("data-composite-key");
		datasetInput.value = "";
		datasetInput.disabled = false;
		datasetInput.classList.remove("input-error");

		const tagGroupInput = inputs[0].querySelector(
			`.${classPrefix}-dataset-tag-group-input`,
		);
		tagGroupInput.value = "";
		tagGroupInput.disabled = false;
		tagGroupInput.classList.remove("input-error");

		if (!isEdit) {
			const tagGroupCheckbox = inputs[0].querySelector(
				`.${classPrefix}-dataset-same-tag-groups-checkbox`,
			);

			if (tagGroupCheckbox) {
				tagGroupCheckbox.checked = false;
			}
			removeListeners(datasetInput, tagGroupInput, tagGroupCheckbox, false);
		} else {
			removeListeners(datasetInput, tagGroupInput, null, true);
		}

		const actionsRow = datasetInput.closest(
			`.${classPrefix}-dataset-row-actions`,
		);
		actionsRow.querySelector(".error-message").innerText = "";

		const uneditableMessage = actionsRow.querySelector(".uneditable-message");
		if (uneditableMessage) {
			uneditableMessage.innerText = "";
		}

		// reset invalid count to 0
		invalidCount = 0;

		// reset save button
		saveButton.disabled = true;
	};

	// function to remove a dataset name row
	wpd.dataSeriesManagement.removeRow = function (event) {
		const index = event.target.closest(".add-dataset-row").dataset.index;
		const rows = container.children;

		// destroy and remove awesomplete instance at the given index
		datasetAutocompletes[index].destroy;
		datasetAutocompletes.splice(index, 1);
		tagGroupAutocompletes[index].destroy;
		tagGroupAutocompletes.splice(index, 1);

		// remove select event handler
		const datasetInput = rows[index].querySelector(
			".add-dataset-study-arm-input",
		);
		const tagGroupInput = rows[index].querySelector(
			".add-dataset-tag-group-input",
		);

		removeListeners(datasetInput, tagGroupInput);

		// remove the dataset name input at the given index
		rows[index].remove();

		const hadDatasetError = datasetInput.classList.contains("input-error");
		const hadTagGroupError = tagGroupInput.classList.contains("input-error");
		if (hadDatasetError) {
			invalidCount--;
		}
		if (hadTagGroupError) {
			invalidCount--;
		}

		wpd.dataSeriesManagement.validateRow(
			rows[0].querySelector(".add-dataset-study-arm-input"),
		);

		// re-index inputs
		if (rows.length > 1) {
			for (let i = 1; i < rows.length; i++) {
				rows[i].closest(".add-dataset-row").dataset.index = i;
				rows[i].querySelector(".dataset-remove-button").dataset.index = i;
				rows[i].querySelector(".add-dataset-number").innerHTML = i + 1;
				wpd.dataSeriesManagement.validateRow(
					rows[i].querySelector(".add-dataset-study-arm-input"),
				);
			}
		}
	};

	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	// function to add a new dataset name input
	wpd.dataSeriesManagement.addRow = function (focusNewInput, isSidebar) {
		const prefix = isSidebar ? "add-" : "";
		container = wpdDocument.getElementById(prefix + "axis-study-branch-inputs");

		const rows = container.children;

		// insert a new input at the end of the container
		container.insertAdjacentHTML(
			"beforeend",
			isSidebar
				? wpd.dataSeriesManagement._generateSidebarDatasetNameInput(rows.length)
				: wpd.dataSeriesManagement._generateDatasetNameInput(rows.length),
		);

		// stamp dataset uuid on the new row; resolved at save time
		container.children[container.children.length - 1].dataset.datasetUuid =
			uuidv4();

		// per-branch broadcast section will pick up the new row once a valid
		// composite key is entered (sync called from validateRow)
		const compositeKeys = wpd.tagGroups.compositeKeys;
		const datasetNames = compositeKeys.map((key) => key.key);

		const newDatasetInput = rows[rows.length - 1].querySelector(
			".add-dataset-study-arm-input",
		);
		const newTagGroupInput = rows[rows.length - 1].querySelector(
			".add-dataset-tag-group-input",
		);

		// initialize awesomplete
		datasetAutocompletes.push(
			this._compositeKeyInput(newDatasetInput, datasetNames),
		);

		tagGroupAutocompletes.push(
			this._tagGroupInput(newTagGroupInput, tagGroups),
		);

		// attach event handlers
		attachListeners(newDatasetInput, newTagGroupInput);

		// disable save button
		saveButton.disabled = true;

		// set focus on the new input if specified
		if (focusNewInput) {
			newDatasetInput.focus();
		}

		// set default tag group
		if (defaultTagGroup !== null) {
			newTagGroupInput.value = defaultTagGroup;
		}
	};

	// row inputs validator
	wpd.dataSeriesManagement.validateRow = function (input, isEdit) {
		const allCompositeKeys = wpd.tagGroups.compositeKeys.map((key) => key.key);

		const row = input.closest(
			isEdit ? ".edit-dataset-row-actions" : ".add-dataset-row-actions",
		);

		const rowIndex = isEdit
			? "0"
			: row.closest(".add-dataset-row").getAttribute("data-index");

		const allRows = Array.from(
			container.querySelectorAll(
				isEdit ? ".edit-dataset-row-actions" : ".add-dataset-row-actions",
			),
		);

		const isAddNew =
			!isEdit && container.id === "add-new-dataset-study-arm-inputs";

		const activeDataset = wpd.tree.getActiveDataset();
		const existingCompositeKeys =
			isEdit || isAddNew
				? wpd.appData
						.getPlotData()
						.getDatasetsForAxis(wpd.tree.getActiveAxes())
						.filter(
							(d) =>
								!!d.getMetadata()?.studyData &&
								(!activeDataset || d.name !== activeDataset.name),
						)
						.map((d) => d.getMetadata().studyData.compositeKey)
				: allRows
						.filter(
							(row) =>
								row.closest(".add-dataset-row").getAttribute("data-index") !==
								rowIndex,
						)
						.map((row) => row.querySelector(".add-dataset-study-arm-input"))
						.filter((input) => input.value !== "")
						.map((input) => input.getAttribute("data-composite-key"));

		const allTagGroups = tagGroups.map((tagGroup) => tagGroup.name);
		const branchInput = row.querySelector(
			isEdit ? ".edit-dataset-study-arm-input" : ".add-dataset-study-arm-input",
		);
		const hasBranchInputError = branchInput.classList.contains("input-error");
		const branchValue = branchInput.getAttribute("data-composite-key");
		const branchText = branchInput.value;

		const isBranchEmpty = _.isEmpty(branchValue);
		const isBranchInvalid = !_.includes(allCompositeKeys, branchValue);
		const isBranchDuplicate = _.includes(existingCompositeKeys, branchValue);

		const tagGroupInput = row.querySelector(
			isEdit ? ".edit-dataset-tag-group-input" : ".add-dataset-tag-group-input",
		);
		const hasTagGroupInputError =
			tagGroupInput.classList.contains("input-error");
		const tagGroupValue = tagGroupInput.value.trim();
		const isTagGroupEmpty = _.isEmpty(tagGroupValue);
		const isTagGroupInvalid = !_.includes(allTagGroups, tagGroupValue);

		const errorContainer = row.querySelector(".error-message");
		const errorMessages = [];
		if (isBranchEmpty) {
			if (!hasBranchInputError) {
				invalidCount++;
				branchInput.classList.add("input-error");
			}
			errorMessages.push("study branch cannot be empty");
		} else if (isBranchInvalid) {
			if (!hasBranchInputError) {
				invalidCount++;
				branchInput.classList.add("input-error");
			}
			errorMessages.push(`${branchValue} is not a valid study branch`);
		} else if (isBranchDuplicate) {
			if (!hasBranchInputError) {
				invalidCount++;
				branchInput.classList.add("input-error");
			}
			errorMessages.push(`${branchText} already exists on this graph`);
		} else {
			if (hasBranchInputError) {
				invalidCount--;
				branchInput.classList.remove("input-error");
			}
		}

		if (isTagGroupEmpty) {
			if (!hasTagGroupInputError) {
				invalidCount++;
				tagGroupInput.classList.add("input-error");
			}
			errorMessages.push("tag group cannot be empty");
		} else if (isTagGroupInvalid) {
			if (!hasTagGroupInputError) {
				invalidCount++;
				tagGroupInput.classList.add("input-error");
			}
			errorMessages.push(`${tagGroupValue} is not a valid tag group`);
		} else {
			if (hasTagGroupInputError) {
				invalidCount--;
				tagGroupInput.classList.remove("input-error");
			}
		}

		const isSidebar = container.id === "add-axis-study-branch-inputs";
		if (invalidCount > 0) {
			const errorMessage = errorMessages.join("; ");
			errorContainer.innerText = errorMessage;
			if (!isSidebar) {
				saveButton.disabled = true;
			}

			wpd.dataSeriesManagement.syncBranchBroadcastSection();
			return false;
		} else {
			errorContainer.innerText = "";
			if (!isSidebar) {
				saveButton.disabled = false;
			}

			wpd.dataSeriesManagement.syncBranchBroadcastSection();
			return true;
		}
	};

	wpd.dataSeriesManagement.validateAll = function (isEdit) {
		const tagGroupInputs = container.getElementsByClassName(
			"add-dataset-tag-group-input",
		);

		let valid = true;
		for (let i = 0; i < tagGroupInputs.length; i++) {
			const result = wpd.dataSeriesManagement.validateRow(
				tagGroupInputs[i],
				isEdit,
			);
			if (!result) {
				valid = false;
			}
		}

		return valid;
	};

	// overwrite rename dataset function to refresh the tree while preserving selection
	wpd.dataSeriesManagement.renameDataset = function () {
		const $dsName = wpdDocument.getElementById("rename-dataset-name-input");

		// add a new event listener to stop propagation
		const keyupHandler = (event) => event.stopPropagation();

		$dsName.removeEventListener("keyup", keyupHandler);
		$dsName.addEventListener("keyup", keyupHandler);

		wpd.popup.close("rename-dataset-popup");

		const ds = wpd.tree.getActiveDataset();
		ds.name = $dsName.value.trim();
		wpd.tree.refreshPreservingSelection();
		wpd.tree.selectPath(`/${wpd.tree.getActiveAxes().name}/${ds.name}`, true);
	};

	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	// new function to handle dataset adding from the add datasets popup
	wpd.dataSeriesManagement.addDatasets = function (isSidebar) {
		const prefix = isSidebar ? "add-" : "";
		container = wpdDocument.getElementById(prefix + "axis-study-branch-inputs");
		if (invalidCount > 0) {
			return;
		}

		const rows = Array.from(container.children);

		// get existing values in the add dataset popup
		const studyBranchKeys = rows
			.map((row) => row.querySelector(".add-dataset-study-arm-input"))
			.filter((input) => input.value !== "")
			.map((input) => input.getAttribute("data-composite-key"));
		const tagGroupNames = rows
			.map((row) => row.querySelector(".add-dataset-tag-group-input"))
			.filter((input) => input.value !== "")
			.map((input) => input.value);

		const plotData = wpd.appData.getPlotData();
		const fileManager = wpd.appData.getFileManager();
		const pageManager = wpd.appData.getPageManager();
		const isMultipage = wpd.appData.isMultipage();

		// clear active dataset
		wpd.tree.setActiveDataset(null);

		const newDatasets = [];
		for (let i = 0; i < studyBranchKeys.length; i++) {
			// reuse the dialog-row-stamped uuid if present, otherwise mint here
			const uuid = rows[i]?.dataset?.datasetUuid || uuidv4();
			const dataset = new wpd.Dataset();

			// create the datasets
			dataset.name = `${studyBranchKeys[i]}_${uuid}`;
			const compositeKey = studyBranchKeys[i];
			const nameValue =
				wpd.CompositeKeyUtils.getCompositeKeyValue(compositeKey);

			dataset.setMetadata({
				name: nameValue,
				uuid: uuid,
				studyData: {
					compositeKey,
					tagGroup: _.chain(tagGroups)
						.find(["name", tagGroupNames[i]])
						.pick(["name", "uuid"])
						.value(),
				},
			});

			// collect newly created datasets
			newDatasets.push(dataset);

			// register the dataset
			plotData.addDataset(dataset);
			fileManager.addDatasetsToCurrentFile([dataset]);

			if (isMultipage) {
				pageManager.addDatasetsToCurrentPage([dataset]);
			}

			// set the first dataset as the active dataset
			if (wpd.tree.getActiveDataset() === null) {
				wpd.tree.setActiveDataset(dataset);
			}

			// dispatch dataset add event
			wpd.events.dispatch("wpd.dataset.add", {
				dataset: dataset,
			});
		}

		// write per-branch broadcast tags (collected from the dialog's section)
		// to the active axes' axesLabelData. Done before resetDialog so the
		// section's DOM is still populated.
		if (!isSidebar) {
			wpd.dataSeriesManagement.writeBranchBroadcastTagsToActiveAxes();
		}

		if (isSidebar) {
			wpd.sidebar.clear();
		} else {
			// reset popup
			wpd.dataSeriesManagement.resetDialog();

			// close popup
			wpd.popup.close("add-dataset-popup");
		}

		// prompt point group creation
		if (wpd.tree.inWizard()) {
			// exit the wizard
			wpd.tree.exitWizard();
		}

		// update datasets associated with this axis
		const axis = plotData.getAxesForDataset(wpd.tree.getActiveDataset());
		if (dataEntryVM.pointGroupOverhaul) {
			wpd.pointGroups.updateAxisDatasetPointGroupsV2(axis);
		} else {
			wpd.pointGroups.updateAxisDatasetPointGroups(axis);
		}

		// update tree
		wpd.tree.refreshPreservingSelection();
	};

	// new function to handle canceling from the add dataset popup
	wpd.dataSeriesManagement.cancelDatasetAdd = function () {
		// reset the popup
		wpd.dataSeriesManagement.resetDialog();

		// close popup
		wpd.popup.close("add-dataset-popup");

		// bail on graph add
		wpd.alignAxes.cancelGraphAdd();
	};

	wpd.dataSeriesManagement.showEditDataset = function () {
		const dataset = wpd.tree.getActiveDataset();
		const metadata = dataset.getMetadata();

		wpd.popup.show("edit-graph-dataset-dialog");

		const compositeKeys = wpd.tagGroups.compositeKeys;
		const datasetNames = compositeKeys.map((key) => key.key);

		container = wpdDocument.getElementById("edit-dataset-study-arm-inputs");
		saveButton = wpdDocument.getElementById("edit-graph-dataset-save-button");

		const datasetInput = container.querySelector(
			".edit-dataset-study-arm-input",
		);
		datasetInput.value = metadata.name;
		datasetInput.setAttribute(
			"data-composite-key",
			metadata.studyData?.compositeKey,
		);

		datasetAutocompletes[0] = this._compositeKeyInput(
			datasetInput,
			datasetNames,
		);

		const tagGroupInput = container.querySelector(
			".edit-dataset-tag-group-input",
		);

		tagGroupInput.value = metadata.studyData?.tagGroup?.name;
		tagGroupAutocompletes[0] = this._tagGroupInput(tagGroupInput, tagGroups);

		attachListeners(datasetInput, tagGroupInput, null, true);

		datasetInput.blur();
	};

	wpd.dataSeriesManagement.saveEditDataset = function () {
		this.saveDataset(true);
		wpd.popup.close("edit-graph-dataset-dialog");
		wpd.dataSeriesManagement.resetDialog(true);
	};

	wpd.dataSeriesManagement.saveDataset = function (isEdit, rowContainer) {
		if (invalidCount > 0) {
			return;
		}

		const currentContainer = rowContainer ?? container;
		const datasetClass = isEdit
			? ".edit-dataset-study-arm-input"
			: ".add-dataset-study-arm-input";
		const datasetInput = currentContainer.querySelector(datasetClass);
		const tagGroupClass = isEdit
			? ".edit-dataset-tag-group-input"
			: ".add-dataset-tag-group-input";
		const tagGroupInput = currentContainer.querySelector(tagGroupClass);

		const compositeKey = datasetInput.getAttribute("data-composite-key");
		const tagGroupValue = tagGroupInput.value.trim();
		const newTagGroup = _.chain(tagGroups)
			.find(["name", tagGroupValue])
			.pick(["name", "uuid"])
			.value();

		const nameValue = wpd.CompositeKeyUtils.getCompositeKeyValue(compositeKey);

		if (!isEdit) {
			const dataset = new wpd.Dataset();
			// reuse the dialog-row-stamped uuid if present, otherwise mint here
			const uuid = rowContainer?.dataset?.datasetUuid || uuidv4();
			dataset.name = `${nameValue}_${uuid}`;
			dataset.setMetadata({
				name: nameValue,
				uuid,
				studyData: {
					compositeKey,
					tagGroup: newTagGroup,
				},
			});

			const plotData = wpd.appData.getPlotData();
			const fileManager = wpd.appData.getFileManager();
			const pageManager = wpd.appData.getPageManager();
			const isMultipage = wpd.appData.isMultipage();
			plotData.addDataset(dataset);
			fileManager.addDatasetsToCurrentFile([dataset]);

			if (isMultipage) {
				pageManager.addDatasetsToCurrentPage([dataset]);
			}

			// dispatch dataset add event
			wpd.events.dispatch("wpd.dataset.add", {
				dataset: dataset,
			});

			// update datasets associated with this axis
			if (dataEntryVM.pointGroupOverhaul) {
				wpd.pointGroups.updateAxisDatasetPointGroupsV2(
					plotData.getAxesForDataset(dataset),
				);
			} else {
				wpd.pointGroups.updateAxisDatasetPointGroups(
					plotData.getAxesForDataset(dataset),
				);
			}
			wpd.tree.refreshPreservingSelection();
		} else {
			const dataset = wpd.tree.getActiveDataset();
			const uuid = dataset.getMetadata().uuid;
			dataset.name = `${nameValue}_${uuid}`;
			const existingMetadata = dataset.getMetadata();
			dataset.setMetadata({
				...existingMetadata,
				name: nameValue,
				uuid,
				studyData: {
					...existingMetadata.studyData,
					compositeKey,
					tagGroup: newTagGroup,
				},
			});

			const [
				studyKeyTag,
				partKeyTag,
				armKeyTag,
				populationKeyTag,
				subgroupKeyTag,
			] = wpd.CompositeKeyUtils.branchTags.map((tag) =>
				_.find(wpd.tagGroups.tags, { name: tag }),
			);

			dataset
				.getAllPixels()
				.filter((pixel) => !!pixel.metadata)
				.forEach((pixel) => {
					pixel.metadata.compositeKey = compositeKey;
					pixel.metadata.tagGroup = {
						...newTagGroup,
						instanceUuid: uuidv4(),
					};
					let mIndex = 0;
					const nonKeyMetadata = Object.keys(pixel.metadata)
						.filter(
							(key) =>
								!isNaN(key) &&
								!wpd.tagGroups.isKeyTag(pixel.metadata[key].tag.name),
						)
						.map((key) => pixel.metadata[key]);

					Object.keys(pixel.metadata)
						.filter((key) => !isNaN(key))
						.forEach((key) => delete pixel.metadata[key]);

					const keyMappings = [
						{ key: "study", tag: studyKeyTag },
						{ key: "studyPart", tag: partKeyTag },
						{ key: "studyArm", tag: armKeyTag },
						{
							key: "population",
							tag: populationKeyTag,
						},
						{
							key: "subgroup",
							tag: subgroupKeyTag,
						},
					];

					const parsedKey =
						wpd.CompositeKeyUtils.parseCompositeKey(compositeKey);

					keyMappings.forEach(({ key, tag }) => {
						const val = parsedKey[key];
						if (val) {
							pixel.metadata[mIndex++] = {
								tag: {
									name: tag.name,
									uuid: tag.uuid,
								},
								value: val,
								hidden: true,
							};
						}
					});

					nonKeyMetadata.forEach((metadatum) => {
						pixel.metadata[mIndex++] = metadatum;
					});
				});
			wpd.tree.refreshPreservingSelection();
			wpd.tree.switchDataset(dataset.name);
		}
	};

	wpd.dataSeriesManagement.cancelEditDataset = function () {
		wpd.dataSeriesManagement.resetDialog(true);
		wpd.popup.close("edit-graph-dataset-dialog");
	};

	// override rename dataset popup function to use display name instead of name
	wpd.dataSeriesManagement.showRenameDataset = function () {
		const dataset = wpd.tree.getActiveDataset();
		const $renameInput = wpdDocument.getElementById(
			"rename-dataset-name-input",
		);

		// get dataset metadata
		const metadata = dataset.getMetadata();

		// check for metadata name
		if (metadata.name) {
			$renameInput.value = metadata.name;
		} else {
			$renameInput.value = dataset.name;
		}

		wpd.popup.show("rename-dataset-popup");
	};

	// override rename dataset logic to recreate ${displayName}_${uuid} for name and remove duplicate check
	wpd.dataSeriesManagement.renameDataset = function () {
		wpd.popup.close("rename-dataset-popup");

		const dataset = wpd.tree.getActiveDataset();

		const $renameInput = wpdDocument.getElementById(
			"rename-dataset-name-input",
		);

		// get dataset metadata
		const metadata = dataset.getMetadata();

		const newName = $renameInput.value.trim();

		let displayName = newName;

		if (metadata.name && metadata.uuid) {
			// update the name in the dataset metadata
			metadata.name = newName;
			dataset.setMetadata(metadata);

			displayName = `${newName}_${metadata.uuid}`;
		}

		dataset.name = displayName;

		wpd.tree.refreshPreservingSelection();
	};

	wpd.dataSeriesManagement.renameKeypress = function (e) {
		if (e.key === "Enter") {
			wpd.dataSeriesManagement.renameDataset();
		}
	};

	wpd.dataSeriesManagement.setSameTagGroupForAll = function () {};

	// ===== Per-branch default tags ("Default tags per study branch") =====
	// Lives in the Graph Study Branches popup. Each branch row in the dialog
	// has a corresponding group of tag/value rows in the section below; on
	// save, entries are written to axesLabelData.broadcastTags with a
	// datasetUuid scope field, alongside the existing graph-wide entries
	// (no scope) from the Axis Data Types dialog.

	const branchBroadcastAwesompletes = [];

	function buildBranchBroadcastTagList() {
		// Suggest only tags configured for graph/table extraction (same
		// filter as the axis-data-type pickers), so deprecated/non-graph
		// tags don't surface here. Unlike those pickers we keep "Unit" and
		// "Time (unit)" available -- they're graph-eligible, and in this
		// section curators may want to apply a single unit (or time unit)
		// to every extracted data point of a branch.
		return dataEntryVM.getGraphEligibleTags();
	}

	function appendBranchBroadcastRow(rowsContainer, prefill) {
		const row = wpdDocument.createElement("div");
		row.className = "broadcast-tag-row";

		const awesompleteWrapper = wpdDocument.createElement("span");
		awesompleteWrapper.className = "awesomplete";
		const tagInput = wpdDocument.createElement("input");
		tagInput.type = "text";
		tagInput.className = "broadcast-tag-name";
		tagInput.placeholder = "Tag name (e.g. Count of participants)";
		awesompleteWrapper.appendChild(tagInput);

		const valueInput = wpdDocument.createElement("input");
		valueInput.type = "text";
		valueInput.className = "broadcast-tag-value";
		valueInput.placeholder = "Optional value (blank = fill in per point)";

		const removeBtn = wpdDocument.createElement("button");
		removeBtn.type = "button";
		removeBtn.className = "broadcast-tag-remove";
		removeBtn.title = "Remove";
		removeBtn.textContent = "×";
		removeBtn.addEventListener("click", (event) => {
			event.preventDefault();
			wpd.dataSeriesManagement.removeBranchBroadcastTagRow(removeBtn);
		});

		row.appendChild(awesompleteWrapper);
		row.appendChild(valueInput);
		row.appendChild(removeBtn);
		rowsContainer.appendChild(row);

		if (prefill) {
			tagInput.value = prefill.tagName || "";
			valueInput.value = prefill.value || "";
		}

		const awe = new Awesomplete(tagInput, {
			list: buildBranchBroadcastTagList(),
			autoFirst: true,
			data: function (item) {
				return { label: item.name, value: item.uuid };
			},
			replace: function (suggestion) {
				this.input.value = suggestion.label;
			},
		});
		branchBroadcastAwesompletes.push(awe);

		return row;
	}

	function appendBranchBroadcastGroup(datasetUuid, branchLabel) {
		const groupsContainer = wpdDocument.getElementById(
			"branch-broadcast-tags-groups",
		);
		const group = wpdDocument.createElement("div");
		group.className = "branch-broadcast-tags-group";
		group.dataset.datasetUuid = datasetUuid;

		const label = wpdDocument.createElement("div");
		label.className = "branch-broadcast-tags-group-label";
		label.textContent = branchLabel;

		const rows = wpdDocument.createElement("div");
		rows.className = "branch-broadcast-tags-group-rows";

		const empty = wpdDocument.createElement("div");
		empty.className = "branch-broadcast-tags-group-empty";
		empty.textContent = "No default tags";
		rows.appendChild(empty);

		const link = wpdDocument.createElement("a");
		link.className = "broadcast-tag-add-link";
		link.href = "#";
		link.textContent = "+ Add default tag";
		link.addEventListener("click", (event) => {
			event.preventDefault();
			wpd.dataSeriesManagement.addBranchBroadcastTagRow(link);
		});

		group.appendChild(label);
		group.appendChild(rows);
		group.appendChild(link);
		groupsContainer.appendChild(group);
		return group;
	}

	wpd.dataSeriesManagement.addBranchBroadcastTagRow = function (
		linkEl,
		prefill,
	) {
		const group = linkEl.closest(".branch-broadcast-tags-group");
		if (!group) return;
		const rowsContainer = group.querySelector(
			".branch-broadcast-tags-group-rows",
		);
		const emptyMsg = rowsContainer.querySelector(
			".branch-broadcast-tags-group-empty",
		);
		if (emptyMsg) emptyMsg.remove();
		const row = appendBranchBroadcastRow(rowsContainer, prefill);
		if (!prefill) {
			row.querySelector(".broadcast-tag-name").focus();
		}
	};

	wpd.dataSeriesManagement.removeBranchBroadcastTagRow = function (buttonEl) {
		const row = buttonEl.closest(".broadcast-tag-row");
		if (!row) return;
		const group = row.closest(".branch-broadcast-tags-group");
		row.remove();
		if (group && !group.querySelector(".broadcast-tag-row")) {
			const rowsContainer = group.querySelector(
				".branch-broadcast-tags-group-rows",
			);
			const empty = wpdDocument.createElement("div");
			empty.className = "branch-broadcast-tags-group-empty";
			empty.textContent = "No default tags";
			rowsContainer.appendChild(empty);
		}
	};

	// Reconcile per-branch groups with the current set of valid branch rows
	// in the popup. Called after row add/remove/validate.
	wpd.dataSeriesManagement.syncBranchBroadcastSection = function () {
		const section = wpdDocument.getElementById("branch-broadcast-tags-section");
		const groupsContainer = wpdDocument.getElementById(
			"branch-broadcast-tags-groups",
		);
		const rowContainer = wpdDocument.getElementById("axis-study-branch-inputs");
		if (!section || !groupsContainer || !rowContainer) return;

		const branchRows = Array.from(rowContainer.children);
		const branchInfo = branchRows
			.map((row) => {
				const dsInput = row.querySelector(".add-dataset-study-arm-input");
				const datasetUuid = row.dataset.datasetUuid;
				const branchLabel = dsInput?.value?.trim() || "";
				const isValid =
					!!datasetUuid &&
					!!branchLabel &&
					!dsInput.classList.contains("input-error");
				return { datasetUuid, branchLabel, isValid };
			})
			.filter((b) => b.isValid);

		if (branchInfo.length === 0) {
			section.setAttribute("hidden", "true");
			groupsContainer.replaceChildren();
			return;
		}
		section.removeAttribute("hidden");

		const existingGroups = Array.from(groupsContainer.children);
		const existingByUuid = new Map(
			existingGroups.map((g) => [g.dataset.datasetUuid, g]),
		);
		const seen = new Set();

		// Add or update groups in branch row order
		for (const branch of branchInfo) {
			let group = existingByUuid.get(branch.datasetUuid);
			if (!group) {
				group = appendBranchBroadcastGroup(
					branch.datasetUuid,
					branch.branchLabel,
				);
			} else {
				const label = group.querySelector(".branch-broadcast-tags-group-label");
				if (label) label.textContent = branch.branchLabel;
				groupsContainer.appendChild(group); // re-append to maintain order
			}
			seen.add(branch.datasetUuid);
		}

		// Drop groups for datasets no longer present
		for (const group of existingGroups) {
			if (!seen.has(group.dataset.datasetUuid)) {
				group.remove();
			}
		}
	};

	// Populate per-branch tag rows from existing axesLabelData.broadcastTags
	// (entries with a datasetUuid). Called after syncBranchBroadcastSection
	// has built the empty groups.
	wpd.dataSeriesManagement.populateBranchBroadcastTags = function (
		broadcastTags,
	) {
		if (!Array.isArray(broadcastTags)) return;
		const groupsContainer = wpdDocument.getElementById(
			"branch-broadcast-tags-groups",
		);
		if (!groupsContainer) return;

		for (const entry of broadcastTags) {
			if (!entry?.datasetUuid) continue;
			const group = groupsContainer.querySelector(
				`.branch-broadcast-tags-group[data-dataset-uuid="${entry.datasetUuid}"]`,
			);
			if (!group) continue;
			const rowsContainer = group.querySelector(
				".branch-broadcast-tags-group-rows",
			);
			const emptyMsg = rowsContainer.querySelector(
				".branch-broadcast-tags-group-empty",
			);
			if (emptyMsg) emptyMsg.remove();
			appendBranchBroadcastRow(rowsContainer, entry);
		}
	};

	wpd.dataSeriesManagement.collectBranchBroadcastTags = function () {
		const groupsContainer = wpdDocument.getElementById(
			"branch-broadcast-tags-groups",
		);
		if (!groupsContainer) return [];
		const result = [];
		const groups = groupsContainer.querySelectorAll(
			".branch-broadcast-tags-group",
		);
		for (const group of groups) {
			const datasetUuid = group.dataset.datasetUuid;
			if (!datasetUuid) continue;
			const rows = group.querySelectorAll(".broadcast-tag-row");
			for (const row of rows) {
				const tagName = row.querySelector(".broadcast-tag-name")?.value?.trim();
				const value = row.querySelector(".broadcast-tag-value")?.value?.trim();
				if (!tagName) continue;
				result.push({ tagName, value: value || "", datasetUuid });
			}
		}
		return result;
	};

	// Merge collected per-branch entries with existing graph-wide entries on
	// the active axes' axesLabelData.broadcastTags. Called from save handlers.
	// Returns the cascade promise so callers can await if needed; in the
	// creation flow there are no existing points so the cascade is a no-op.
	wpd.dataSeriesManagement.writeBranchBroadcastTagsToActiveAxes = function () {
		const axes = wpd.tree.getActiveAxes();
		if (!axes) return Promise.resolve();
		const md = axes.getMetadata();
		const axesLabelData = md.axesLabelData || (md.axesLabelData = {});
		const before = axesLabelData.broadcastTags || [];
		const graphWide = before.filter((e) => !e.datasetUuid);
		const branchScoped = wpd.dataSeriesManagement.collectBranchBroadcastTags();
		const after = [...graphWide, ...branchScoped];
		axesLabelData.broadcastTags = after;
		axes.setMetadata(md);
		return wpd.alignAxes.cascadeBroadcastTagsChange(axes, before, after);
	};
}

export { init };
